import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ChevronDown,
  ChevronUp,
  CircleInfo,
} from '@unified-checkout/design-system/icons/index';
import {
  Button,
  FormatCurrencyForDisplay,
  SkeletonBase,
  Tooltip,
} from '@unified-checkout/design-system';

import type {
  ILabelValueProps,
  ISummaryCard,
  ISummaryCardItem,
} from '@app/dashboard/types/innerLayout/balance_and_transfers/transfers/summary';

import { getValueLabelObject } from '../../utils';

import styles from './summary-card.module.scss';

const SummaryCard = ({
  title,
  itemsList,
  width,
  height,
  collapsible = {},
  isLoading,
}: ISummaryCard) => {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation();

  const visibleRows = expanded
    ? itemsList
    : itemsList.slice(0, collapsible.visibleCount);

  const showToggle =
    collapsible.showCollapseButton &&
    itemsList.length > collapsible.visibleCount!;

  const getRowCollapseState = (idx: number) => {
    if (collapsible.renderAsRow) return 'row-mode';

    if (!expanded && idx === collapsible.visibleCount! - 1)
      return 'overlay-last-collapsed';

    if (expanded && idx === itemsList.length - 1)
      return 'overlay-last-expanded';

    return undefined;
  };

  const handleExpandClick = () => {
    setExpanded((prev) => !prev);
    collapsible.expandableButton?.onClickHandler(!expanded);
  };

  const getRowDataAttributes = (item: ISummaryCardItem, idx: number) => {
    const labelObj = getValueLabelObject(item.label);
    const valueObj = getValueLabelObject(item.value);

    return {
      'data-summary-card-bolded-label': labelObj.isBold,
      'data-summary-card-bolded-value': valueObj.isBold,
      'data-summary-card-status-label': labelObj.status,
      'data-summary-card-status-value': valueObj.status,
      'data-summary-card-collapse-state': getRowCollapseState(idx),
    };
  };

  const renderLabel = (item: ISummaryCardItem) => {
    const { label } = item;
    if (!label) return null;

    if (typeof label !== 'string' && label.renderAs) return label.renderAs;

    return (
      <div className={styles['summary-card__item-label-content']}>
        <p className={styles['summary-card__item-label-content-text']}>
          {typeof label === 'string' ? label : label.text}
        </p>
        {item.toolTipProps && (
          <Tooltip placement="top start" {...item.toolTipProps} centerArrow>
            <div className={styles['summary-card__item-label-tooltip-icon']}>
              {CircleInfo}
            </div>
          </Tooltip>
        )}
      </div>
    );
  };

  const renderValue = (value: string | ILabelValueProps | undefined) => {
    if (!value) return null;

    if (typeof value === 'string') {
      return (
        <div className={styles['summary-card__item-value']}>
          <p className={styles['summary-card__item-value-text']}>
            {FormatCurrencyForDisplay(value)}
          </p>
        </div>
      );
    }

    if (value.renderAs) return value.renderAs;

    if (!value.text) return null;

    return (
      <div className={styles['summary-card__item-value']}>
        <p className={styles['summary-card__item-value-text']}>
          {FormatCurrencyForDisplay(value.text)}
        </p>
        {value.hintText && (
          <p className={styles['summary-card__item-value-hint-text']}>
            {value.hintText}
          </p>
        )}
      </div>
    );
  };

  return (
    <div className={styles['summary-card']} style={{ width, height }}>
      {title && <p className={styles['summary-card__title']}>{title}</p>}
      <div className={styles['summary-card__items-list']}>
        {visibleRows.map((item, idx) => {
          return (
            <div
              className={
                styles[
                  `summary-card__item` +
                    (item.rowClassName ? ` ${item.rowClassName}` : '')
                ]
              }
              key={`summary-card-item-${idx}`}
              {...getRowDataAttributes(item, idx)}
            >
              {isLoading ? (
                <SkeletonBase
                  key={`summary-card-skeleton-loader-${idx}`}
                  height="2rem"
                />
              ) : (
                <>
                  <div className={styles['summary-card__item-label']}>
                    {item.labelLeadingIcon && (
                      <div
                        className={
                          styles['summary-card__item-label-leading-icon']
                        }
                      >
                        {item.labelLeadingIcon}
                      </div>
                    )}
                    {renderLabel(item)}
                  </div>

                  {renderValue(item.value)}
                </>
              )}
            </div>
          );
        })}
        {showToggle && (
          <div className={styles['summary-card__expand-button']}>
            <Button
              text={expanded ? t('common.seeLess') : t('common.seeMore')}
              trailingIcon={expanded ? ChevronUp : ChevronDown}
              variant="link"
              color="primary"
              size="md"
              {...collapsible.expandableButton}
              onClickHandler={handleExpandClick}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SummaryCard;
